import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ApiResponse,
  GetResetPasswordInfoResponse,
} from '@models/ApiResponse';
import { AppError } from '@models/appError';
import { LmsApi } from '@services/_api/lms-api';
import {
  ApiEndpointsService,
  ApiHttpErrorService,
  ApiHttpService,
  AuthenticationService,
  HttpSpinnerService,
  RouteNavigationService,
  ToastService,
} from '@services/index';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css'],
})
export class ResetPasswordComponent implements OnInit {
  submitted = false;
  resetPasswordInfo: GetResetPasswordInfoResponse;
  token: string;
  passwordResetCompleted = false;
  showPassword = false;
  showConfirmPassword = false;

  passwordForm: FormGroup;
  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private httpService: ApiHttpService,
    private endpointsService: ApiEndpointsService,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private httpSpinnerService: HttpSpinnerService,
    private routeNavService: RouteNavigationService,
    private httpErrorService: ApiHttpErrorService
  ) {}

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return this.passwordResetCompleted;
  }

  ngOnInit() {
    this.initPasswordForm();
    this.token = this.route.snapshot.queryParams['token'];
    this.httpSpinnerService.show();
    this.httpService
      .post<ApiResponse<GetResetPasswordInfoResponse>>(
        this.endpointsService.getResetPasswordInfo(),
        {
          token: this.token,
        }
      )
      .pipe(
        take(1),
        finalize(() => {
          this.httpSpinnerService.hide();
        })
      )
      .subscribe(
        (res) => {
          this.resetPasswordInfo = res.data;
        },
        (error: AppError) => {
          this.httpErrorService.handle(error);
          this.passwordResetCompleted = true;
          this.routeNavService.goToHome();
        }
      );
  }

  get password() {
    return this.passwordForm?.get('password');
  }

  get confirmPassword() {
    return this.passwordForm?.get('confirmPassword');
  }

  get passwordsMatch() {
    return this.password.value === this.confirmPassword.value;
  }

  public tryResetPassword() {
    this.submitted = true;
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    if (!this.passwordsMatch) {
      return;
    }

    this.resetPassword();
  }

  private resetPassword() {
    const completeRegistrationRequest = {
      password: this.password.value,
      confirmPassword: this.confirmPassword.value,
      accountId: this.resetPasswordInfo.accountId,
      tenantId: this.resetPasswordInfo.tenantId,
      token: this.token,
    };

    this.httpService
      .post<ApiResponse<LmsApi.AuthenticateResponse>>(
        this.endpointsService.resetPassword(),
        completeRegistrationRequest
      )
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (res.succeeded && res.data.jwtToken) {
            this.passwordResetCompleted = true;
            this.toastService.showSuccess('Password reset successfuly!');
            this.authService.updateUserSubject(res.data);
            sessionStorage.setItem(
              'currentTenant',
              JSON.stringify(res.data.tenant)
            );
            this.authService.updateTenantSubject(res.data.tenant);
            this.authService.startRefreshTokenTimer(
              res.data.jwtToken,
              res.data.id
            );
          }
          this.routeNavService.login();
        },
        (error: AppError) => {
          this.httpErrorService.handle(error);
        }
      );
  }

  private initPasswordForm() {
    this.passwordForm = this.fb.group({
      password: [
        '',
        [
          Validators.required,
          Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$'),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$'),
        ],
      ],
    });
  }
}
